import { useEffect, useRef } from 'react'

/**
 * Creates a body-level div to use as `container` prop for F0Dialog.
 * This is needed because the document preview renders at body level via react-portal
 * with z-index 998, while F0Dialog defaults to portaling inside #content (inside #factorialRoot).
 * By portaling to a body-level sibling with z-index 999, the dialog appears above the preview.
 *
 * The container uses position:fixed + inset:0 to cover the viewport and z-index:999
 * to sit above the preview overlay (z-index:998). F0Dialog's internal fixed-position
 * elements still participate in this stacking context.
 *
 * `pointer-events: none` keeps the empty container from swallowing clicks across the
 * whole viewport when the dialog isn't open / between the dialog's own interactive
 * elements. F0Dialog's backdrop and content re-enable pointer-events on themselves.
 */
export function useDialogPortalContainer() {
  const containerRef = useRef<HTMLDivElement | null>(null)

  if (!containerRef.current) {
    const div = document.createElement('div')
    div.setAttribute('data-document-preview-dialog-portal', '')
    div.style.position = 'fixed'
    div.style.inset = '0'
    div.style.zIndex = '999'
    div.style.pointerEvents = 'none'
    document.body.appendChild(div)
    containerRef.current = div
  }

  useEffect(() => {
    return () => {
      if (containerRef.current && containerRef.current.parentNode) {
        containerRef.current.parentNode.removeChild(containerRef.current)
        containerRef.current = null
      }
    }
  }, [])

  return containerRef.current
}
